import { motion } from "framer-motion";
import { WORKFLOW } from "@/constants/testIds";
import { useT } from "@/lib/i18n";

const DURATIONS = ["T+0 → T+2", "T+2 → T+5", "T+5 → T+9", "T+9 → SHIP"];

export const Workflow = () => {
  const { t } = useT();
  const w = t.workflow;
  return (
    <section
      id="process"
      data-testid={WORKFLOW.section}
      className="relative px-6 md:px-12 lg:px-24 py-24 md:py-32 border-b border-white/10"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-20">
        <div className="md:col-span-5">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#ff3b00] mb-4">
            {w.eyebrow}
          </div>
          <h2 className="font-display uppercase text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight">
            {w.titleA}<br />
            <span className="text-white/40">{w.titleB}</span>
          </h2>
        </div>
        <div className="md:col-span-6 md:col-start-7 self-end">
          <p className="font-mono text-sm md:text-base text-white/60 leading-relaxed">
            {w.lede}
          </p>
        </div>
      </div>

      <div className="border border-white/10 bg-[#0a0a0a]">
        <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-white/10 font-mono text-[10px] md:text-xs tracking-[0.25em] uppercase text-white/40">
          <span>{w.terminalCmd}</span>
          <span className="hidden sm:flex items-center gap-2">
            <span className="w-2 h-2 bg-[#ff3b00] inline-block" />
            {w.compiling}
            <span className="blink text-[#ff3b00]">_</span>
          </span>
        </div>

        <ol className="divide-y divide-white/10">
          {w.steps.map((step, i) => {
            const num = String(i + 1).padStart(2, "0");
            return (
              <motion.li
                key={i}
                data-testid={WORKFLOW.step(i)}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 }}
                className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 px-4 md:px-8 py-8 md:py-10 hover:bg-[#141414] transition-colors duration-300"
              >
                <div className="md:col-span-2 flex md:flex-col gap-3 md:gap-1 items-baseline md:items-start">
                  <span className="font-display text-5xl md:text-6xl leading-none text-[#ff3b00]">
                    {num}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
                    {DURATIONS[i]}
                  </span>
                </div>

                <div className="md:col-span-6">
                  <h3 className="font-display uppercase text-2xl md:text-3xl leading-[1] tracking-tight text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="font-mono text-sm leading-relaxed text-white/60">
                    {step.body}
                  </p>
                </div>

                <div className="md:col-span-3 md:col-start-10 font-mono text-[10px] tracking-[0.25em] uppercase text-white/40 md:text-right self-center">
                  <span className="inline-block px-2 py-1 border border-white/15 group-hover:border-[#ff3b00] group-hover:text-[#ff3b00] transition-colors">
                    {w.stageLabel}&nbsp;{num}&nbsp;·&nbsp;{w.stageOk}
                  </span>
                </div>
              </motion.li>
            );
          })}
        </ol>

        <div className="flex items-center justify-between px-4 md:px-6 py-3 border-t border-white/10 font-mono text-[10px] md:text-xs tracking-[0.25em] uppercase text-white/40">
          <span>{w.exit}<span className="text-[#ff3b00]">{w.exitTag}</span></span>
          <span>{w.accuracy}</span>
        </div>
      </div>
    </section>
  );
};
